import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Sparkles, CheckCircle, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const toneOptions = [
  { value: 'luxury', label: 'Luxury', desc: 'Sophisticated, elegant, refined' },
  { value: 'corporate', label: 'Corporate', desc: 'Professional, trustworthy, authoritative' },
  { value: 'fun', label: 'Fun & Energetic', desc: 'Upbeat, engaging, playful' },
  { value: 'casual', label: 'Casual & Friendly', desc: 'Approachable, warm, relatable' },
  { value: 'edgy', label: 'Edgy & Bold', desc: 'Modern, daring, innovative' }
];

const genderOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-Binary' },
  { value: 'neutral', label: 'No Preference' }
];

const languageOptions = [
  'English', 'Spanish', 'French', 'Mandarin', 'Portuguese', 
  'German', 'Italian', 'Japanese', 'Arabic', 'Russian', 
  'Korean', 'Hindi', 'Dutch'
];

const CustomAmbassadorRequest = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);
  
  const [formData, setFormData] = useState({
    brand_name: '',
    brand_style: '',
    preferred_gender: '',
    preferred_look: '',
    tone: '',
    use_case: '',
    languages_needed: ['English'],
    additional_notes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addLanguage = (lang) => {
    if (!formData.languages_needed.includes(lang)) {
      setFormData(prev => ({
        ...prev,
        languages_needed: [...prev.languages_needed, lang]
      }));
    }
  };

  const removeLanguage = (lang) => {
    if (formData.languages_needed.length > 1) {
      setFormData(prev => ({
        ...prev,
        languages_needed: prev.languages_needed.filter(l => l !== lang)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand_name || !formData.tone) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/ambassadors/custom-request', formData);
      setRequestId(response.data.request_id);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="custom-ambassador-success">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Button variant="ghost" onClick={() => navigate('/ambassadors')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to AI Ambassadors
            </Button>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-white text-center">
            <CardContent className="py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Request Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Your custom AI Ambassador request has been received. Our team will review it and get back to you within 2-3 business days.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground">Request ID</p>
                <p className="font-mono text-lg">{requestId}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-amber-800 mb-2">What's Next?</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Our team will review your brand requirements</li>
                  <li>• We'll send you a detailed quote and timeline</li>
                  <li>• Upon approval, we'll create your custom AI Ambassador</li>
                  <li>• You'll receive notification when it's ready to use</li>
                </ul>
              </div>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => navigate('/ambassadors')}>
                  Browse AI Ambassadors
                </Button>
                <Button onClick={() => navigate('/client/home')}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="custom-ambassador-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/ambassadors')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to AI Ambassadors
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Create Custom AI Ambassador</h1>
          <p className="text-muted-foreground">
            Build a unique AI representative tailored to your brand
          </p>
        </div>

        {/* Pricing Info */}
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg">Custom Creation Fee</h3>
                <p className="text-white/90">Starting at $1,500</p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-sm text-white/80">Includes design, development, and testing</p>
                <p className="text-sm text-white/80">+ per-activation usage fees apply</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle>Brand Information</CardTitle>
              <CardDescription>Tell us about your brand and vision</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="brand_name">Brand Name *</Label>
                <Input
                  id="brand_name"
                  value={formData.brand_name}
                  onChange={(e) => handleInputChange('brand_name', e.target.value)}
                  placeholder="Your company or brand name"
                  required
                  data-testid="brand-name-input"
                />
              </div>

              <div>
                <Label htmlFor="brand_style">Brand Style & Aesthetic</Label>
                <Textarea
                  id="brand_style"
                  value={formData.brand_style}
                  onChange={(e) => handleInputChange('brand_style', e.target.value)}
                  placeholder="Describe your brand's visual style, colors, and overall aesthetic..."
                  rows={3}
                  data-testid="brand-style-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle>Ambassador Preferences</CardTitle>
              <CardDescription>Define the look and feel of your AI Ambassador</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Tone *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {toneOptions.map((tone) => (
                    <div
                      key={tone.value}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        formData.tone === tone.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleInputChange('tone', tone.value)}
                      data-testid={`tone-${tone.value}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{tone.label}</span>
                        {formData.tone === tone.value && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{tone.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="preferred_gender">Preferred Gender/Presentation</Label>
                <Select 
                  value={formData.preferred_gender} 
                  onValueChange={(value) => handleInputChange('preferred_gender', value)}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select preference" />
                  </SelectTrigger>
                  <SelectContent>
                    {genderOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="preferred_look">Preferred Look & Appearance</Label>
                <Textarea
                  id="preferred_look"
                  value={formData.preferred_look}
                  onChange={(e) => handleInputChange('preferred_look', e.target.value)}
                  placeholder="Describe the desired appearance, age range, style of dress, etc..."
                  rows={3}
                  data-testid="preferred-look-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle>Languages & Use Case</CardTitle>
              <CardDescription>Define capabilities and intended use</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Languages Needed</Label>
                <div className="flex flex-wrap gap-2 mt-2 mb-3">
                  {formData.languages_needed.map((lang) => (
                    <Badge key={lang} variant="secondary" className="flex items-center gap-1">
                      {lang}
                      {formData.languages_needed.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLanguage(lang)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
                <Select onValueChange={addLanguage}>
                  <SelectTrigger data-testid="language-select">
                    <SelectValue placeholder="Add language..." />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions
                      .filter(l => !formData.languages_needed.includes(l))
                      .map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {lang}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Additional languages may incur extra fees (+$100-250)
                </p>
              </div>

              <div>
                <Label htmlFor="use_case">Primary Use Case</Label>
                <Textarea
                  id="use_case"
                  value={formData.use_case}
                  onChange={(e) => handleInputChange('use_case', e.target.value)}
                  placeholder="Describe how you plan to use this AI Ambassador (events, campaigns, etc.)..."
                  rows={3}
                  data-testid="use-case-input"
                />
              </div>

              <div>
                <Label htmlFor="additional_notes">Additional Notes</Label>
                <Textarea
                  id="additional_notes"
                  value={formData.additional_notes}
                  onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                  placeholder="Any other details, requirements, or questions..."
                  rows={3}
                  data-testid="additional-notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/ambassadors')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !formData.brand_name || !formData.tone}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90"
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomAmbassadorRequest;
